/**
 * Complete deletion: see the list, retype the phrase, and only then.
 *
 * FRONTEND_DESIGN §4.10: "Complete deletion first shows every app-controlled
 * category to be removed and explicitly says that moved exports, OS backups,
 * and provider-retained data are outside DaVinci's control. Confirmation
 * requires retyping a short phrase and uses the backend's short-lived deletion
 * token."
 *
 * Every list on this screen comes from the preview the server returned. None
 * of it is written here. That is the difference between a screen that shows
 * what will be removed and a screen that shows what someone once believed
 * would be removed: a category added on the server appears here with no change
 * to this file, and a category the server stops removing disappears from the
 * screen at the same moment it stops being true.
 *
 * The retyped phrase comes from the server too, for the same reason the token
 * does — a frontend that chose its own phrase could choose an empty one. What
 * this file adds is the refusal to send a confirmation whose phrase does not
 * match, which is a courtesy: the server checks it again and is the guarantee.
 *
 * The dialog is not time-limited (§8). The *token* expires server-side, and
 * the deadline is shown rather than enforced — a confirmation that vanished
 * mid-decision would be a dialog that decided for you.
 */

import { useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { DeleteAllDataResponse, DeletionPreviewResponse } from '@davinci/contracts';
import { ApiRequestError } from '../../api/client.js';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { ConfirmDialog } from './confirm-dialog.js';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

const messages = defineMessages({
  en: {
    heading: 'Delete everything',
    intro1:
      'This removes every file DaVinci controls and leaves an empty installation behind. There is no undo, and after Phase 1 there is evidence behind it that cannot be observed again.',
    intro2:
      'DaVinci will show you exactly what it is about to remove, from the same code that does the removing \u2014 not from a list written separately and hoped to be accurate.',
    checking: 'Checking…',
    show: 'Show me what would be deleted',
    deleteEllipsis: 'Delete everything…',
    dialogTitle: 'Delete everything DaVinci holds?',
    dialogBody:
      'Everything listed on this page will be removed. DaVinci will stop, and the next launch will be a first run.',
    phraseMismatch: (phrase: string) =>
      `The phrase does not match. Type ${phrase} in the box below, exactly, or press Cancel.`,
    typeToConfirm: (phrase: string) => `Type ${phrase} to confirm`,
    retyping: 'Retyping it is what separates deciding from clicking. Nothing else on this screen is irreversible.',
    cancel: 'Cancel',
    deleting: 'Deleting…',
    deleteConfirm: 'Delete everything',
    expired:
      'This confirmation is no longer valid \u2014 it expired, or a newer preview replaced it. Nothing was deleted. Close this and show the list again.',
    notAccepted: 'DaVinci did not accept that phrase. Nothing was deleted.',
    whereLives: 'Where your data lives',
    whereLivesBody: 'Everything DaVinci knows is inside one folder on this machine:',
    wouldBeRemoved: 'What would be removed',
    notPresent: '(not present)',
    holdsFiles: '(holds files)',
    presentEmpty: '(present, empty)',
    tablesHold: 'Tables that hold something',
    noTables: 'None of your tables hold anything yet.',
    notReachedHeading: 'What a per-learner deletion would not reach',
    notReachedHint:
      'This deletion removes the whole folder, so these go too. They are named because DaVinci\u2019s completeness here comes from removing files rather than from walking your records \u2014 which is a gap, not a design choice.',
    cannotDelete: 'What DaVinci cannot delete',
    expiresAfter: (time: string) =>
      `This confirmation stops working shortly after ${time}. Nothing on this screen closes itself \u2014 if it expires while you are reading, ask for the list again.`,
    database: 'The database',
    blobs: 'Stored files',
    imports: 'Imported source material',
    backups: 'Backups DaVinci made',
    logs: 'Local logs',
    deletionReceipts: 'Records of previous deletions',
    unexpected: 'Something DaVinci did not put here, and will not remove',
  },
  he: {
    heading: 'מחיקת הכול',
    intro1:
      'פעולה זו מסירה כל קובץ ש-DaVinci שולט בו ומשאירה התקנה ריקה. אין ביטול, ואחרי שלב 1 יש מאחוריה ראיות שאי אפשר לצפות בהן שוב.',
    intro2:
      'DaVinci יראה לך בדיוק מה הוא עומד להסיר, מאותו קוד שמבצע את ההסרה \u2014 לא מרשימה שנכתבה בנפרד ושמקווים שהיא מדויקת.',
    checking: 'בדיקה…',
    show: 'הצגת מה יימחק',
    deleteEllipsis: 'מחיקת הכול…',
    dialogTitle: 'למחוק את כל מה ש-DaVinci מחזיק?',
    dialogBody: 'כל מה שמופיע בעמוד הזה יוסר. DaVinci ייעצר, וההפעלה הבאה תהיה הפעלה ראשונה.',
    phraseMismatch: (phrase: string) =>
      `הביטוי לא תואם. יש להקליד ${phrase} בתיבה שלמטה, בדיוק כפי שהוא, או ללחוץ על ביטול.`,
    typeToConfirm: (phrase: string) => `יש להקליד ${phrase} לאישור`,
    retyping: 'הקלדה מחדש היא מה שמבדיל בין החלטה ללחיצה. שום דבר אחר במסך הזה אינו בלתי הפיך.',
    cancel: 'ביטול',
    deleting: 'מחיקה…',
    deleteConfirm: 'מחיקת הכול',
    expired:
      'האישור הזה כבר לא תקף \u2014 הוא פג, או שתצוגה מקדימה חדשה יותר החליפה אותו. שום דבר לא נמחק. יש לסגור את החלון ולהציג את הרשימה שוב.',
    notAccepted: 'DaVinci לא קיבל את הביטוי הזה. שום דבר לא נמחק.',
    whereLives: 'איפה הנתונים שלך שמורים',
    whereLivesBody: 'כל מה ש-DaVinci יודע נמצא בתיקייה אחת במכשיר הזה:',
    wouldBeRemoved: 'מה יוסר',
    notPresent: '(לא קיים)',
    holdsFiles: '(מכיל קבצים)',
    presentEmpty: '(קיים, ריק)',
    tablesHold: 'טבלאות שמחזיקות משהו',
    noTables: 'עדיין אין בטבלאות שלך שום דבר.',
    notReachedHeading: 'מה שמחיקה של לומד בודד לא הייתה מגיעה אליו',
    notReachedHint:
      'המחיקה הזו מסירה את התיקייה כולה, ולכן גם אלה נמחקים. הם מוזכרים כי השלמות של DaVinci כאן נובעת מהסרת קבצים ולא מסריקת הרשומות שלך \u2014 וזו פרצה, לא בחירת עיצוב.',
    cannotDelete: 'מה ש-DaVinci לא יכול למחוק',
    expiresAfter: (time: string) =>
      `האישור הזה מפסיק לעבוד זמן קצר אחרי ${time}. שום דבר במסך הזה לא נסגר מעצמו \u2014 אם הוא פג בזמן הקריאה, יש לבקש את הרשימה שוב.`,
    database: 'מסד הנתונים',
    blobs: 'קבצים שמורים',
    imports: 'חומר מקור שיובא',
    backups: 'גיבויים ש-DaVinci יצר',
    logs: 'יומנים מקומיים',
    deletionReceipts: 'תיעוד של מחיקות קודמות',
    unexpected: 'משהו ש-DaVinci לא הניח כאן, ולא יסיר',
  },
});

type DeletionMessages = (typeof messages)['en'];

const CATEGORY_KEYS: Readonly<Record<string, keyof DeletionMessages>> = {
  database: 'database',
  blobs: 'blobs',
  imports: 'imports',
  backups: 'backups',
  logs: 'logs',
  'deletion-receipts': 'deletionReceipts',
  unexpected: 'unexpected',
};

/** The categories, in words. Falls back to the server's own name — see below. */

export interface DeletionPanelProps {
  /** Called with the receipt once the deletion has happened. */
  readonly onDeleted: (receipt: DeleteAllDataResponse) => void;
}

export function DeletionPanel({ onDeleted }: DeletionPanelProps): React.JSX.Element {
  const t = useMessages(messages);
  const api = useApi();
  const headingId = useId();
  const dialogHeadingId = useId();
  const dialogBodyId = useId();
  const phraseFieldId = useId();

  const [confirming, setConfirming] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [phraseRejected, setPhraseRejected] = useState(false);

  const preview = useMutation({
    mutationFn: () => api.deletionPreview(),
    onSuccess: () => {
      setConfirming(false);
      setPhrase('');
      setPhraseRejected(false);
    },
  });

  const confirm = useMutation({
    mutationFn: (body: { token: string; confirmationPhrase: string }) => api.deleteAllData(body),
    onSuccess: (receipt) => {
      setConfirming(false);
      onDeleted(receipt);
    },
  });

  const listing = preview.data;

  function handleConfirm(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (listing === undefined) return;
    if (phrase.trim() !== listing.confirmationPhrase) {
      // No request, and the dialog stays open with the field still holding
      // what was typed. §8 asks an error to identify the field and the
      // recovery action, which is all the alert below says.
      setPhraseRejected(true);
      return;
    }
    setPhraseRejected(false);
    confirm.mutate({ token: listing.token.value, confirmationPhrase: phrase.trim() });
  }

  return (
    <section className="panel panel-destructive" aria-labelledby={headingId}>
      <h2 id={headingId}>{t.heading}</h2>
      <p>{t.intro1}</p>
      <p>{t.intro2}</p>

      <button type="button" onClick={() => preview.mutate()} disabled={preview.isPending}>
        {preview.isPending ? t.checking : t.show}
      </button>

      {preview.isError ? (
        <FailureNotice error={preview.error} onRetry={() => preview.mutate()} />
      ) : null}

      {listing !== undefined ? (
        <div className="panel-result">
          <DeletionListing listing={listing} bodyId={dialogBodyId} />

          <button type="button" className="button-destructive" onClick={() => setConfirming(true)}>
            {t.deleteEllipsis}
          </button>

          {confirm.isError && !confirming ? <DeletionFailure error={confirm.error} /> : null}
        </div>
      ) : null}

      {confirming && listing !== undefined ? (
        <ConfirmDialog
          labelledBy={dialogHeadingId}
          describedBy={dialogBodyId}
          onDismiss={() => setConfirming(false)}
        >
          <h2 id={dialogHeadingId}>{t.dialogTitle}</h2>
          <p>{t.dialogBody}</p>

          {confirm.isError ? <DeletionFailure error={confirm.error} /> : null}

          {phraseRejected ? (
            <div className="notice notice-error" role="alert">
              <p>{t.phraseMismatch(listing.confirmationPhrase)}</p>
            </div>
          ) : null}

          <form onSubmit={handleConfirm} noValidate>
            <div className="field">
              <label htmlFor={phraseFieldId}>
                {t.typeToConfirm(listing.confirmationPhrase)}
              </label>
              <p className="field-hint" id={`${phraseFieldId}-hint`}>
                {t.retyping}
              </p>
              <input
                id={phraseFieldId}
                name="confirmationPhrase"
                type="text"
                value={phrase}
                autoComplete="off"
                aria-describedby={`${phraseFieldId}-hint`}
                onChange={(event) => {
                  setPhrase(event.target.value);
                  setPhraseRejected(false);
                }}
              />
            </div>

            <div className="dialog-actions">
              <button type="button" onClick={() => setConfirming(false)}>
                {t.cancel}
              </button>
              <button type="submit" className="button-destructive" disabled={confirm.isPending}>
                {confirm.isPending ? t.deleting : t.deleteConfirm}
              </button>
            </div>
          </form>
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

/**
 * A refusal the founder can act on.
 *
 * The two 409s mean different things and have different repairs, which is why
 * the server sends different codes for them: an expired or superseded token
 * needs a fresh preview, a mismatched phrase needs retyping. Collapsing them
 * into one sentence would tell someone to start over when they need to fix a
 * word — and after Phase 1, starting over means reading a list of everything
 * they are about to lose a second time.
 */
function DeletionFailure({ error }: { readonly error: unknown }): React.JSX.Element {
  const t = useMessages(messages);
  if (error instanceof ApiRequestError && error.code === 'confirmation_expired') {
    return (
      <div className="notice notice-error" role="alert">
        <p>{t.expired}</p>
      </div>
    );
  }
  if (error instanceof ApiRequestError && error.code === 'confirmation_phrase_mismatch') {
    return (
      <div className="notice notice-error" role="alert">
        <p>{t.notAccepted}</p>
      </div>
    );
  }
  return <FailureNotice error={error} />;
}

/** Every list on this screen, rendered from what the server reported. */
function DeletionListing({
  listing,
  bodyId,
}: {
  readonly listing: DeletionPreviewResponse;
  readonly bodyId: string;
}): React.JSX.Element {
  const t = useMessages(messages);
  return (
    <div id={bodyId}>
      <h3>{t.whereLives}</h3>
      <p>
        {t.whereLivesBody} <code>{listing.dataRoot}</code>
      </p>

      <h3>{t.wouldBeRemoved}</h3>
      <ul className="plain-list">
        {listing.categories.map((category) => (
          <li key={`${category.kind}-${category.path}`}>
            <strong>{categoryLabel(t, category.kind)}</strong> —{' '}
            <code>{category.path}</code>{' '}
            {/*
             * Absent categories are listed too, and say so. "backups: not
             * present" is information; leaving it out would make "nothing to
             * remove" and "not looked at" look the same.
             */}
            {!category.present
              ? t.notPresent
              : category.holdsFiles
                ? t.holdsFiles
                : t.presentEmpty}
          </li>
        ))}
      </ul>

      <h3>{t.tablesHold}</h3>
      {listing.tablesHoldingData.length > 0 ? (
        <ul className="plain-list">
          {listing.tablesHoldingData.map((table) => (
            <li key={table}>
              <code>{table}</code>
            </li>
          ))}
        </ul>
      ) : (
        <p>{t.noTables}</p>
      )}

      {listing.retainedByDesign.length > 0 ? (
        <>
          <h3>{t.notReachedHeading}</h3>
          <p className="field-hint">{t.notReachedHint}</p>
          <ul className="plain-list">
            {listing.retainedByDesign.map((row) => (
              <li key={row.table}>
                <code>{row.table}</code> — {row.reason}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h3>{t.cannotDelete}</h3>
      <ul className="plain-list">
        {listing.outsideOurControl.map((limit) => (
          <li key={limit}>{limit}</li>
        ))}
      </ul>

      <p>{t.expiresAfter(utcTimeOf(listing.token.expiresAt))}</p>
    </div>
  );
}

/** See the note on the identical helper in export-panel.tsx. */
function categoryLabel(t: DeletionMessages, kind: string): string {
  const key = CATEGORY_KEYS[kind];
  return key === undefined ? kind : (t[key] as string);
}

function utcTimeOf(iso: string): string {
  return `${iso.slice(11, 19)} UTC`;
}
